import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Input, Button, Card, message, Checkbox, Alert } from 'antd'
import { UserOutlined, LockOutlined } from '@ant-design/icons'
import { authApi } from '@/api/auth'
import type { LoginCredentials } from '@/types/auth'

const REMEMBER_EMAIL_KEY = 'xmanager_remembered_email'

export const Login = () => {
  const [loading, setLoading] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [showMFAVerify, setShowMFAVerify] = useState(false)
  const [challengeSession, setChallengeSession] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const navigate = useNavigate()
  const [loginForm] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const [mfaForm] = Form.useForm()

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY)
    if (savedEmail) {
      loginForm.setFieldsValue({ email: savedEmail })
      setRememberMe(true)
    }
  }, [loginForm])

  const onLogin = async (values: LoginCredentials) => {
    setLoading(true)
    try {
      // Save or clear email based on remember me checkbox
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, values.email)
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY)
      }

      const response = await authApi.login(values)

      // Check if there's a password challenge
      if (response.data.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setEmail(values.email)
        setChallengeSession(response.data.session!)
        setShowPasswordChange(true)
        message.info('Please set a new password to continue')
      }
      // Check if there's an MFA challenge
      else if (response.data.challengeName === 'SOFTWARE_TOKEN_MFA') {
        setEmail(values.email)
        setChallengeSession(response.data.session!)
        setShowMFAVerify(true)
        message.info('Please enter your MFA code')
      } else if (response.success && response.data.user) {
        // Normal login success
        localStorage.setItem('auth_token', response.data.accessToken!)
        localStorage.setItem('refresh_token', response.data.refreshToken!)
        localStorage.setItem('auth_user', JSON.stringify(response.data.user))
        message.success('Login successful!')
        navigate('/', { replace: true })
        window.location.reload() // Reload to update auth context
      }
    } catch (error: unknown) {
      // Provide specific error messages based on error type
      let errorMessage = 'Login failed. Please check your credentials.'

      // Check if it's an axios error with a response
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { status?: number; data?: { error?: { message?: string } } }
        }

        if (!axiosError.response) {
          errorMessage = 'Cannot connect to server. Please check your internet connection.'
        } else if (axiosError.response.status === 500) {
          errorMessage = 'Server error. Please try again later or contact support.'
        } else if (axiosError.response.status === 503) {
          errorMessage = 'Service temporarily unavailable. Please try again in a few moments.'
        } else if (axiosError.response.data?.error?.message) {
          errorMessage = axiosError.response.data.error.message
        }
      }

      message.error(errorMessage)
      console.error('Login error:', {
        status:
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { status?: number } }).response?.status
            : 'unknown',
        message: error instanceof Error ? error.message : String(error),
        data:
          error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: unknown } }).response?.data
            : undefined,
      })
    } finally {
      setLoading(false)
    }
  }

  const onPasswordChange = async (values: { newPassword: string; confirmPassword: string }) => {
    setLoading(true)
    try {
      const response = await authApi.completeNewPassword({
        email,
        newPassword: values.newPassword,
        session: challengeSession,
      })

      if (response.success && response.data.user) {
        // Password change successful, complete login
        localStorage.setItem('auth_token', response.data.accessToken!)
        localStorage.setItem('refresh_token', response.data.refreshToken!)
        localStorage.setItem('auth_user', JSON.stringify(response.data.user))
        message.success('Password changed successfully!')
        navigate('/', { replace: true })
        window.location.reload() // Reload to update auth context
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: { message?: string } } } }).response?.data
              ?.error?.message || 'Failed to change password. Please try again.'
          : 'Failed to change password. Please try again.'
      message.error(errorMessage)
      console.error('Password change error:', error)
    } finally {
      setLoading(false)
    }
  }

  const onMFAVerify = async (values: { mfaCode: string }) => {
    setLoading(true)
    try {
      const response = await authApi.verifyMFA({
        email,
        mfaCode: values.mfaCode,
        session: challengeSession,
      })

      if (response.success && response.data.user) {
        // MFA verification successful, complete login
        localStorage.setItem('auth_token', response.data.accessToken!)
        localStorage.setItem('refresh_token', response.data.refreshToken!)
        localStorage.setItem('auth_user', JSON.stringify(response.data.user))
        message.success('Login successful!')
        navigate('/', { replace: true })
        window.location.reload() // Reload to update auth context
      }
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: { message?: string } } } }).response?.data
              ?.error?.message || 'Invalid MFA code. Please try again.'
          : 'Invalid MFA code. Please try again.'
      message.error(errorMessage)
      console.error('MFA verification error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    setShowPasswordChange(false)
    setShowMFAVerify(false)
    setChallengeSession('')
    setEmail('')
    passwordForm.resetFields()
    mfaForm.resetFields()
  }

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#f0f2f5',
      }}
    >
      {!showPasswordChange && !showMFAVerify ? (
        <Card title="X-Manager Login" style={{ width: 400 }}>
          <Form
            form={loginForm}
            name="login"
            onFinish={onLogin}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: 'Please input your email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Email" size="large" />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Please input your password!' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Password" size="large" />
            </Form.Item>

            <Form.Item>
              <Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}>
                Remember my email
              </Checkbox>
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                Log in
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ) : showMFAVerify ? (
        <Card title="Multi-Factor Authentication" style={{ width: 400 }}>
          <Alert
            message="MFA Verification Required"
            description="Enter the 6-digit code from your authenticator app to complete login."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Form
            form={mfaForm}
            name="mfaVerify"
            onFinish={onMFAVerify}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="mfaCode"
              label="MFA Code"
              rules={[
                { required: true, message: 'Please enter your MFA code' },
                { len: 6, message: 'Code must be exactly 6 digits' },
                { pattern: /^\d+$/, message: 'Code must contain only numbers' },
              ]}
            >
              <Input
                placeholder="000000"
                maxLength={6}
                size="large"
                style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8 }}
                autoFocus
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                Verify Code
              </Button>
            </Form.Item>

            <Form.Item>
              <Button block size="large" onClick={handleBackToLogin}>
                Back to Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
      ) : (
        <Card title="Change Password" style={{ width: 400 }}>
          <Alert
            message="Password Change Required"
            description="You must set a new password before continuing. Your password must be at least 12 characters and contain uppercase, lowercase, numbers, and special characters."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          <Form
            form={passwordForm}
            name="passwordChange"
            onFinish={onPasswordChange}
            autoComplete="off"
            layout="vertical"
          >
            <Form.Item
              name="newPassword"
              label="New Password"
              rules={[
                { required: true, message: 'Please input your new password!' },
                { min: 12, message: 'Password must be at least 12 characters' },
                {
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                  message:
                    'Password must contain uppercase, lowercase, number, and special character',
                },
              ]}
              hasFeedback
            >
              <Input.Password prefix={<LockOutlined />} placeholder="New Password" size="large" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label="Confirm Password"
              dependencies={['newPassword']}
              hasFeedback
              rules={[
                { required: true, message: 'Please confirm your password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('Passwords do not match!'))
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="Confirm Password"
                size="large"
              />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                Change Password
              </Button>
            </Form.Item>

            <Form.Item>
              <Button block size="large" onClick={handleBackToLogin}>
                Back to Login
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}
    </div>
  )
}
